import {
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  mergeClasses,
  tokens,
  shorthands,
} from '@fluentui/react-components';
import React from 'react';

export type DialogOptions = {
  onCloseDialog: () => void;
  inSeparateWindow: boolean;
};

const useStylesWindow = makeStyles({
  rootSurface: {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
  },
  rootContainer: {
    height: '100vh',
    width: '100vw',
    overflow: 'hidden',
    display: 'grid',
    gridTemplateColumns: 'auto min-content',
    gridTemplateRows: 'min-content auto min-content',
    backgroundColor: tokens.colorNeutralBackground3,
  },
  headerContainer: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorBrandBackground2,
    fontSize: tokens.fontSizeBase600,
    padding: tokens.spacingHorizontalM,
    ...shorthands.gridArea(1, 1, 2, 3),
  },
  contentContainer: {
    padding: tokens.spacingHorizontalS,
    ...shorthands.gridArea(2, 1, 3, 3),
    overflowY: 'scroll',
  },
  contentContainerInset: {
    backgroundColor: tokens.colorNeutralBackground1,
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
  },
  footerContainer: {
    padding: tokens.spacingHorizontalM,
    ...shorthands.gridArea(3, 2, 4, 3),
  },
});

export function AppDialogSurface(
  props: React.PropsWithChildren<{ dialogOptions: DialogOptions; className?: string }>,
): JSX.Element {
  const windowStyles = useStylesWindow();
  if (props.dialogOptions.inSeparateWindow) {
    return <div className={mergeClasses(props.className, windowStyles.rootSurface)}>{props.children}</div>;
  }
  return <DialogSurface className={props.className}>{props.children}</DialogSurface>;
}

export function AppDialogBody(
  props: React.PropsWithChildren<{ dialogOptions: DialogOptions; className?: string }>,
): JSX.Element {
  const styles = useStylesWindow();
  if (props.dialogOptions.inSeparateWindow) {
    return <div className={mergeClasses(styles.rootContainer, props.className)}>{props.children}</div>;
  }
  return <DialogBody className={props.className}>{props.children}</DialogBody>;
}
export function AppDialogHeader(props: React.PropsWithChildren<{ dialogOptions: DialogOptions }>): JSX.Element {
  const styles = useStylesWindow();
  if (props.dialogOptions.inSeparateWindow) {
    return <div className={styles.headerContainer}>{props.children}</div>;
  }

  return <DialogTitle>{props.children}</DialogTitle>;
}

export function AppDialogContent(
  props: React.PropsWithChildren<{ dialogOptions: DialogOptions; className?: string }>,
): JSX.Element {
  const styles = useStylesWindow();
  if (props.dialogOptions.inSeparateWindow) {
    return (
      <div className={mergeClasses(styles.contentContainer)}>
        <div className={mergeClasses(styles.contentContainerInset, props.className)}>{props.children}</div>
      </div>
    );
  }
  return <DialogContent className={props.className}>{props.children}</DialogContent>;
}

export function AppDialogFooter(props: React.PropsWithChildren<{ dialogOptions: DialogOptions }>): JSX.Element {
  const styles = useStylesWindow();
  if (props.dialogOptions.inSeparateWindow) {
    return <div className={styles.footerContainer}>{props.children}</div>;
  }
  return <>{props.children}</>;
}
